import type { Node, NodeProps } from '@xyflow/react';
import React, { useMemo } from 'react';
import { cn } from '../../../helpers/util';
import { useAppSelector } from '../../../redux/hooks';
import { selectBarriersLengthByEscalationFactorId, selectMoving } from '../../../redux/slices/diagram';
import BaseNode, { type BaseNodeData } from './base-node.component';
import AddEscalationFactorBarrierControl from './common/add-escalation-factor-barrier-control.component';

type EscalationFactorNodeData = BaseNodeData & {
  controlRecordId: number; // The control record ID this EF is linked to (from backend)
  controlNodeId?: string; // The control node ID this EF is linked to (diagram node id)
};
export type EscalationFactorNodeType = Node<EscalationFactorNodeData>;
type EscalationFactorNodeProps = NodeProps<EscalationFactorNodeType>;

// Amber/yellow styling for escalation factor (CAA convention - 20% opacity applied via CSS)
const className = 'bg-ef-bg border-ef-border text-ef-fg border-dashed';
const iconClassName = 'text-ef-bg stroke-ef-fg';
const staticData = { className, iconClassName };

/**
 * A component that represents an escalation factor (erosion factor) node in a bowtie diagram.
 * Escalation factors connect to causes/consequences and can have escalation factor barriers.
 *
 * @component
 * @param {EscalationFactorNodeProps} props - The properties for the escalation factor node component
 * @returns {JSX.Element} A rendered escalation factor node component
 *
 * @example
 * ```tsx
 * <EscalationFactorNode
 *   id="escalation-factor-1"
 *   data={{ label: "Erosion Factor" }}
 *   position={{ x: 100, y: 100 }}
 * />
 * ```
 */
const EscalationFactorNode = (props: EscalationFactorNodeProps): React.JSX.Element => {
  const { recordId, cellIndex, editMode, controlRecordId, controlNodeId } = props.data;
  const moving = useAppSelector(selectMoving);
  const barriersLength = useAppSelector((state) => selectBarriersLengthByEscalationFactorId(state, props.id));

  const hasBarriers = barriersLength > 0;

  const data = useMemo(() => {
    const modifiedClassName = cn(staticData.className, { 'animate-wobble': moving });
    return {
      ...props.data,
      ...staticData,
      className: modifiedClassName,
      showRecordIndicators: true,
      showUnlinkActionIcon: !props.data.recordId || !hasBarriers,
    };
  }, [props.data, moving]);

  return (
    <div className="group flex items-center">
      <BaseNode {...props} data={data} />

      <AddEscalationFactorBarrierControl
        id={props.id} // escalation factor node id
        escalationFactorRecordId={recordId}
        controlNodeId={controlNodeId}
        controlRecordId={controlRecordId}
        index={cellIndex!}
        disabled={Boolean(moving || editMode)}
      />
    </div>
  );
};

export default EscalationFactorNode;
export const id = 'escalation-factor-node';
